import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProductConfigurationResponse } from '../../api/ConfigApi';
import {
	PRICE_PREVIEW_CHANGES_TABLE_COMMISSIONS_TITLE,
	PRICE_PREVIEW_CHANGES_TABLE_COMMISSION_HOLDBACKS_TITLE,
	PRICE_PREVIEW_CHANGES_TABLE_FEES_TITLE,
	PRICE_PREVIEW_CHANGES_TABLE_HOLDBACKS_TITLE,
	PRICE_PREVIEW_CHANGES_TABLE_NEW_EXPOSURE_LABEL,
	PRICE_PREVIEW_CHANGES_TABLE_SUMMARY_TITLE,
	PRICE_PREVIEW_CHANGES_TABLE_TAXES_TITLE,
	PRICE_PREVIEW_CHANGE_TABLE_HEADER,
} from '../../data/labelsAndMessages';
import {
	AutomatedUnderwritingResult,
	HoldbackResponse,
	Policy2,
	PolicyPriceChangeResponse,
} from '../../types/policy';
import {
	formatCurrency,
	getEntityNameFrom_DescriptionField,
	getLastActiveExposureCharacteristic,
	getLastActivePerilCharacteristic,
	sortObjectsByString,
} from '../../util/util';
import { PaddedDiv, StyledTitleBolded } from '../common';
import UnderWritingDecisionsTable from './UnderwritingDecisions';

interface Props {
	productConfig: ProductConfigurationResponse;
	priceData: PolicyPriceChangeResponse;
	policy: Policy2;
	automatedUnderwritingResult?: AutomatedUnderwritingResult;
}

const PricePreviewChangesTables = (props: Props) => {
	const { t } = useTranslation();
	const { priceData, productConfig, policy, automatedUnderwritingResult } =
		props;

	const renderNoPricing = () => {
		return <div style={{ textAlign: 'center' }}>No pricing available</div>;
	};

	if (!priceData) {
		return renderNoPricing();
	}

	const { policyConfiguration } = productConfig;
	const {
		fees,
		grossFeesChange,
		grossPremiumChange,
		taxGroups,
		grossTaxesChange,
		commissions,
		grossCommissionsChange,
		exposurePrices,
		totalChange,
		holdbacks,
	} = priceData;
	const defaultCurrency = policy?.currency;

	const renderChangeTableHeader = () => {
		return (
			<TableHead>
				<TableRow>
					<TableCell style={primaryTableCellWidth} />
					<TableCell style={secondaryCellWidth} align='right'>
						{PRICE_PREVIEW_CHANGE_TABLE_HEADER()}
					</TableCell>
				</TableRow>
			</TableHead>
		);
	};

	const primaryTableCellWidth = { width: '40%' };
	const secondaryCellWidth = { width: '30%' };
	const exposurePreviews = exposurePrices?.map((exposurePrice, index) => {
		const { locator: exposurePriceLocator, name: exposureName } = exposurePrice;
		let { perilPrices } = exposurePrice;
		const exposureConfig = policyConfiguration.exposures.find(
			(exp) => exp.name === exposureName,
		);
		let exposureDisplayName = exposureConfig.displayName;
		if (exposurePriceLocator) {
			const exposureObj = policy.exposures.find(
				(e) => e.locator === exposurePriceLocator,
			);
			const exposureCharacteristic =
				getLastActiveExposureCharacteristic(exposureObj);
			exposureDisplayName =
				getEntityNameFrom_DescriptionField(exposureCharacteristic) ||
				exposureConfig.displayName;

			if (exposureObj) {
				// get peril price names
				perilPrices = perilPrices.map((perilPrice) => {
					const perilObj = exposureObj.perils.find(
						(p) => p.locator === perilPrice.locator,
					);
					const perilConfig = exposureConfig.perils.find(
						(p) => p.name === perilPrice.name,
					);
					if (perilObj) {
						const perilCharacteristic =
							getLastActivePerilCharacteristic(perilObj);
						perilPrice.displayName =
							getEntityNameFrom_DescriptionField(perilCharacteristic) ||
							perilConfig.displayName;
					} else {
						perilPrice.displayName = perilConfig.displayName;
					}
					return perilPrice;
				});
			}
		}
		const exposureTitle = `${t('exposure.singular.upper')} ${
			index + 1
		}: ${exposureDisplayName} ${
			exposurePrice.newlyAdded || !exposurePrice.locator
				? PRICE_PREVIEW_CHANGES_TABLE_NEW_EXPOSURE_LABEL()
				: ''
		}`;
		const { grossPremiumChange: exposureGrossPremiumChange } = exposurePrice;

		let sortedPerilPrices = sortObjectsByString(perilPrices, 'displayName');
		//  filter out all peril rows with a 0 amount change value
		sortedPerilPrices = sortedPerilPrices.filter(
			(perilPrice) => parseFloat(perilPrice.grossPremiumChange) !== 0,
		);

		const perilRows = sortedPerilPrices.map((perilPrice, idx) => {
			const { grossPremiumChange: grossPremiumChangePeril } = perilPrice;

			const grossPremiumDisplay = grossPremiumChangePeril
				? formatCurrency(parseFloat(grossPremiumChangePeril), policy?.currency)
				: 'N / A';
			const perilConfig = exposureConfig.perils.find(
				(p) => p.name === perilPrice.name,
			);

			return (
				<TableRow key={`${idx}`}>
					<TableCell style={primaryTableCellWidth}>
						{perilPrice.displayName || perilConfig.displayName}
					</TableCell>
					<TableCell style={secondaryCellWidth} align='right'>
						{grossPremiumDisplay}
					</TableCell>
				</TableRow>
			);
		});

		return (
			<PaddedDiv
				className={'exposure-change'}
				data-cy={`exposure-${index}`}
				key={`${exposurePriceLocator}-${index}`}
			>
				<StyledTitleBolded>{exposureTitle}</StyledTitleBolded>
				<TableContainer>
					<Table size='small'>
						{renderChangeTableHeader()}
						<TableBody>
							{perilRows}
							<TableRow>
								<TableCell
									style={{ ...primaryTableCellWidth, fontWeight: 'bold' }}
								>
									Gross Premium
								</TableCell>
								<TableCell
									style={{ ...secondaryCellWidth, fontWeight: 'bold' }}
									align='right'
								>
									{formatCurrency(
										parseFloat(exposureGrossPremiumChange),
										policy?.currency,
									)}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</PaddedDiv>
		);
	});

	const renderFeesTable = () => {
		if (fees && fees.length > 0) {
			const feeRows = fees.map((fee, index) => {
				const { name: feeName, newAmount, amountChange } = fee;
				const feeConfig = policyConfiguration.fees.find(
					(f) => f.name === feeName,
				);
				const feeTitle = feeConfig
					? `Fee ${index + 1}: ${feeConfig.displayName}`
					: '';
				const amtCurrency = policy?.currency;

				// const amount = newAmount ? formatCurrency(parseFloat(newAmount, 10) : ''
				if (newAmount) {
					return (
						<TableRow key={`fee-${index}`}>
							<TableCell style={primaryTableCellWidth}>{feeTitle}</TableCell>
							<TableCell style={secondaryCellWidth} align='right'>
								{formatCurrency(parseFloat(amountChange), amtCurrency)}
							</TableCell>
						</TableRow>
					);
				} else {
					return null;
				}
			});

			return (
				<PaddedDiv data-cy='fees-table'>
					<StyledTitleBolded>
						{PRICE_PREVIEW_CHANGES_TABLE_FEES_TITLE()}
					</StyledTitleBolded>
					<TableContainer>
						<Table size='small'>
							{renderChangeTableHeader()}
							<TableBody>
								{feeRows}
								<TableRow key={'feeTotal'}>
									<TableCell
										style={{ ...primaryTableCellWidth, fontWeight: 'bold' }}
									>
										Total Fees
									</TableCell>
									<TableCell
										style={{ ...secondaryCellWidth, fontWeight: 'bold' }}
										align='right'
									>
										{formatCurrency(
											parseFloat(grossFeesChange),
											defaultCurrency,
										)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</PaddedDiv>
			);
		}

		return null;
	};

	const renderTaxesTable = () => {
		if (taxGroups && taxGroups.length > 0) {
			const taxRows = taxGroups.map((tax, index) => {
				const { name, newAmount, change } = tax;
				const taxConfig = policyConfiguration.taxes.find(
					(f) => f.name === name,
				);
				const taxTitle = taxConfig
					? `Tax ${index + 1}: ${taxConfig.displayName}`
					: '';

				if (newAmount) {
					return (
						<TableRow key={`tax-${index}`}>
							<TableCell style={primaryTableCellWidth}>{taxTitle}</TableCell>
							<TableCell style={secondaryCellWidth} align='right'>
								{formatCurrency(parseFloat(change), defaultCurrency)}
							</TableCell>
						</TableRow>
					);
				} else {
					return null;
				}
			});

			return (
				<PaddedDiv data-cy='taxes-table'>
					<StyledTitleBolded>
						{PRICE_PREVIEW_CHANGES_TABLE_TAXES_TITLE()}
					</StyledTitleBolded>
					<TableContainer>
						<Table size='small'>
							{renderChangeTableHeader()}
							<TableBody>
								{taxRows}
								<TableRow key={'taxTotal'}>
									<TableCell
										style={{ ...primaryTableCellWidth, fontWeight: 'bold' }}
									>
										Total Taxes
									</TableCell>
									<TableCell
										style={{ ...secondaryCellWidth, fontWeight: 'bold' }}
										align='right'
									>
										{formatCurrency(
											parseFloat(grossTaxesChange),
											defaultCurrency,
										)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</PaddedDiv>
			);
		}

		return null;
	};

	const renderHoldbackSummaryRow = () => {
		if (!holdbacks || !holdbacks.length) {
			return null;
		}
		const filteredHoldbacks = holdbacks.filter((h) => h.type !== 'commission');
		const holdbackChange = filteredHoldbacks.reduce(
			(a: number, b: HoldbackResponse) =>
				b.isReversed ? a - parseFloat(b.amount) : a + parseFloat(b.amount),
			0,
		);
		return (
			<TableRow>
				<TableCell style={primaryTableCellWidth}>Holdbacks</TableCell>
				<TableCell style={secondaryCellWidth} align='right'>
					{formatCurrency(
						parseFloat(holdbackChange.toString()),
						defaultCurrency,
					)}
				</TableCell>
			</TableRow>
		);
	};

	const renderSummaryTable = () => {
		return (
			<>
				<StyledTitleBolded>
					{PRICE_PREVIEW_CHANGES_TABLE_SUMMARY_TITLE()}
				</StyledTitleBolded>
				<TableContainer>
					<Table size='small'>
						{renderChangeTableHeader()}
						<TableBody>
							<TableRow>
								<TableCell style={primaryTableCellWidth}>
									Gross Premium
								</TableCell>
								<TableCell style={secondaryCellWidth} align='right'>
									{formatCurrency(
										parseFloat(grossPremiumChange),
										defaultCurrency,
									)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell style={primaryTableCellWidth}>Fees</TableCell>
								<TableCell style={secondaryCellWidth} align='right'>
									{formatCurrency(parseFloat(grossFeesChange), defaultCurrency)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell style={primaryTableCellWidth}>Taxes</TableCell>
								<TableCell style={secondaryCellWidth} align='right'>
									{formatCurrency(
										parseFloat(grossTaxesChange),
										defaultCurrency,
									)}
								</TableCell>
							</TableRow>
							{renderHoldbackSummaryRow()}
							<TableRow>
								<TableCell
									style={{ ...primaryTableCellWidth, fontWeight: 'bold' }}
								>
									Totals
								</TableCell>
								<TableCell
									style={{ ...secondaryCellWidth, fontWeight: 'bold' }}
									align='right'
								>
									{formatCurrency(parseFloat(totalChange), defaultCurrency)}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</>
		);
	};

	const renderHoldbacks = () => {
		if (!holdbacks || !holdbacks.length) {
			return null;
		}
		const premiumHoldbacks = holdbacks.filter((h) => h.type === 'premium');
		const getTotal = (a: number, b: HoldbackResponse) =>
			b.isReversed ? a - parseFloat(b.amount) : a + parseFloat(b.amount);
		const premiumHoldbackTotal = premiumHoldbacks.reduce(getTotal, 0);
		const feeHoldbacks = holdbacks.filter((h) => h.type === 'fee');
		const feeHoldbackTotal = feeHoldbacks.reduce(getTotal, 0);
		const taxHoldbacks = holdbacks.filter((h) => h.type === 'tax');
		const taxHoldbackTotal = taxHoldbacks.reduce(getTotal, 0);
		const totalHoldbacks = holdbacks.filter((h) => h.type !== 'commission');
		const totalHoldbackAmt = totalHoldbacks.reduce(getTotal, 0);
		return (
			<PaddedDiv>
				<StyledTitleBolded>
					{PRICE_PREVIEW_CHANGES_TABLE_HOLDBACKS_TITLE()}
				</StyledTitleBolded>
				<TableContainer>
					<Table size='small'>
						{renderChangeTableHeader()}
						<TableBody>
							<TableRow>
								<TableCell style={primaryTableCellWidth}>Premium</TableCell>
								<TableCell style={secondaryCellWidth} align='right'>
									{formatCurrency(
										parseFloat(premiumHoldbackTotal.toString()),
										defaultCurrency,
									)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell style={primaryTableCellWidth}>Fees</TableCell>
								<TableCell style={secondaryCellWidth} align='right'>
									{formatCurrency(
										parseFloat(feeHoldbackTotal.toString()),
										defaultCurrency,
									)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell style={primaryTableCellWidth}>Taxes</TableCell>
								<TableCell style={secondaryCellWidth} align='right'>
									{formatCurrency(
										parseFloat(taxHoldbackTotal.toString()),
										defaultCurrency,
									)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell
									style={{ ...primaryTableCellWidth, fontWeight: 'bold' }}
								>
									Total
								</TableCell>
								<TableCell
									style={{ ...secondaryCellWidth, fontWeight: 'bold' }}
									align='right'
								>
									{formatCurrency(
										parseFloat(totalHoldbackAmt.toString()),
										defaultCurrency,
									)}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</PaddedDiv>
		);
	};

	const renderCommissions = () => {
		if (!commissions || !commissions.length) {
			return null;
		}
		return (
			<PaddedDiv>
				<StyledTitleBolded>
					{PRICE_PREVIEW_CHANGES_TABLE_COMMISSIONS_TITLE()}
				</StyledTitleBolded>
				<TableContainer>
					<Table size='small'>
						{renderChangeTableHeader()}
						<TableBody>
							{renderCommissionRows()}
							<TableRow>
								<TableCell
									style={{ ...primaryTableCellWidth, fontWeight: 'bold' }}
								>
									Total Commissions
								</TableCell>
								<TableCell
									style={{ ...secondaryCellWidth, fontWeight: 'bold' }}
									align='right'
								>
									{formatCurrency(
										parseFloat(grossCommissionsChange.toString()),
										defaultCurrency,
									)}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</PaddedDiv>
		);
	};

	const renderCommissionRows = () => {
		return commissions.map((commission, index) => {
			const { recipient, amountChange } = commission;
			const commissionTitle = `Recipient ${index + 1}: ${recipient}`;
			return (
				<TableRow key={`commission-${index}`}>
					<TableCell style={primaryTableCellWidth}>{commissionTitle}</TableCell>
					<TableCell style={secondaryCellWidth} align='right'>
						{formatCurrency(
							parseFloat(amountChange.toString()),
							defaultCurrency,
						)}
					</TableCell>
				</TableRow>
			);
		});
	};

	const renderCommissionsHoldbacks = () => {
		const commissionHoldbacks =
			holdbacks && holdbacks.filter((h) => h.type === 'commission');
		if (!commissionHoldbacks || !commissionHoldbacks.length) {
			return null;
		}
		const getTotal = (a: number, b: HoldbackResponse) =>
			b.isReversed ? a - parseFloat(b.amount) : a + parseFloat(b.amount);
		const commissionHoldbackTotal = commissionHoldbacks.reduce(getTotal, 0);
		return (
			<PaddedDiv>
				<StyledTitleBolded>
					{PRICE_PREVIEW_CHANGES_TABLE_COMMISSION_HOLDBACKS_TITLE()}
				</StyledTitleBolded>
				<TableContainer>
					<Table size='small'>
						{renderChangeTableHeader()}
						<TableBody>
							<TableRow>
								<TableCell
									style={{ ...primaryTableCellWidth, fontWeight: 'bold' }}
								>
									Total Commission Holdbacks
								</TableCell>
								<TableCell
									style={{ ...secondaryCellWidth, fontWeight: 'bold' }}
									align='right'
								>
									{formatCurrency(
										parseFloat(commissionHoldbackTotal.toString()),
										defaultCurrency,
									)}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</PaddedDiv>
		);
	};

	const renderUnderwritingDecisions = () => {
		return automatedUnderwritingResult ? (
			<UnderWritingDecisionsTable
				underwritingData={automatedUnderwritingResult}
				basePolicyLocator={policy.locator}
			/>
		) : null;
	};

	return (
		<>
			{renderSummaryTable()}
			{exposurePreviews}
			{renderFeesTable()}
			{renderTaxesTable()}
			{renderHoldbacks()}
			{renderCommissions()}
			{renderCommissionsHoldbacks()}
			{renderUnderwritingDecisions()}
		</>
	);
};

export default PricePreviewChangesTables;
